import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { BarChart3, Download, Users, TrendingUp, PieChart, Clock, Building2 } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { BarChart } from '@/shared/components/Charts/BarChart';
import { LineChart } from '@/shared/components/Charts/LineChart';
import { useUser } from '@/shared/hooks/useAuth';
import {
  useAdminStats,
  type AdminEmployeeStat,
} from '@/features/dashboard/widgets/admin/hooks/useAdminStats';
import { useCompanyStats, useWorkHours, type WorkHoursRow, type DepartmentRow } from '../hooks/useReports';
import { localToday } from '@/shared/utils/utils';

const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  it: 'IT',
  digital_marketing: 'Digital Mktg',
  management: 'Management',
};

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  interested: 'Interested',
  converted: 'Converted',
  rejected: 'Rejected',
};

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="p-4">
      <p className="text-display-sm font-semibold text-on-surface">{value}</p>
      <p className="mt-1 text-body-sm text-on-surface-variant">{label}</p>
    </Card>
  );
}

function todayISO() {
  return localToday();
}

function secToHours(sec: number) {
  return Math.round((sec / 3600) * 100) / 100;
}

export function ReportsPage() {
  const user = useUser();
  const isPrivileged = ['admin', 'superadmin'].includes(user?.role ?? '');
  const isSuperAdmin = user?.role === 'superadmin';

  const { data: stats, isLoading: statsLoading, isError: statsError } = useAdminStats();
  const [whDate, setWhDate] = useState(todayISO());
  const { data: workHours, isLoading: whLoading } = useWorkHours(whDate, isPrivileged);
  const { data: company, isLoading: companyLoading } = useCompanyStats(isSuperAdmin);

  // Declared before any early return so hooks run unconditionally in the same order.
  const leaderboardColumns = useMemo<Column<AdminEmployeeStat>[]>(() => {
    const base: Column<AdminEmployeeStat>[] = [
      { key: 'name', header: 'Employee', render: (r) => <span className="font-medium text-on-surface">{r.display_name}</span> },
    ];
    if (isSuperAdmin || user?.department === 'sales') {
      return [
        ...base,
        { key: 'total_leads', header: 'Total Leads', align: 'right', render: (r) => r.total_leads },
        { key: 'leads_week', header: 'This Week', align: 'right', render: (r) => r.leads_week },
        { key: 'leads_today', header: 'Today', align: 'right', render: (r) => r.leads_today },
        { key: 'contacted', header: 'Contacted', align: 'right', render: (r) => r.contacted },
        { key: 'converted', header: 'Converted', align: 'right', render: (r) => r.converted },
      ];
    }
    return [
      ...base,
      { key: 'tasks_done', header: 'Tasks Done', align: 'right', render: (r) => r.tasks_done ?? 0 },
      { key: 'tasks_open', header: 'Tasks Open', align: 'right', render: (r) => r.tasks_open ?? 0 },
      { key: 'hours_week', header: 'Hours (Week)', align: 'right', render: (r) => `${r.hours_week ?? 0}h` },
    ];
  }, [isSuperAdmin, user?.department]);

  if (!isPrivileged) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState title="Reports unavailable" description="Reports are available to administrators only." />
      </div>
    );
  }

  const overview = stats?.overview;
  const statusData = (stats?.statusBreakdown ?? []).map((s) => ({
    status: STATUS_LABELS[s.status] ?? s.status,
    n: s.n,
  }));
  const activityData = stats?.dailyActivity ?? [];
  const leaderboard = stats?.byEmployee ?? [];

  const handleExport = () => {
    window.location.href = '/api/leads/export.csv';
    toast.success('CSV download started');
  };

  const workHoursColumns: Column<WorkHoursRow>[] = [
    { key: 'name', header: 'Employee', render: (r) => <span className="font-medium text-on-surface">{r.display_name}</span> },
    { key: 'department', header: 'Dept', render: (r) => DEPT_LABELS[r.department] ?? r.department ?? '—' },
    { key: 'check_in', header: 'In', render: (r) => r.check_in || '—' },
    { key: 'check_out', header: 'Out', render: (r) => r.check_out || '—' },
    { key: 'break', header: 'Break (min)', align: 'right', render: (r) => Math.round((r.break_sec || 0) / 60) },
    { key: 'net', header: 'Net Hours', align: 'right', render: (r) => `${secToHours(r.net_sec || 0)}h` },
  ];

  const deptColumns: Column<DepartmentRow>[] = [
    { key: 'department', header: 'Department', render: (r) => DEPT_LABELS[r.department] ?? r.department ?? '—' },
    { key: 'total', header: 'Total Staff', align: 'right', render: (r) => r.total },
    { key: 'active_count', header: 'Active', align: 'right', render: (r) => r.active_count },
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={BarChart3}
        eyebrow="Insights"
        title="Reports"
        description={<>Team performance, pipeline, and work-hour analytics.</>}
        actions={
          <>{(isSuperAdmin || user?.department === 'sales') && (
                  <Button variant="outline" onClick={handleExport}>
                    <Download className="h-4 w-4" /> Export Leads CSV
                  </Button>
                )}</>
        }
      />

      {statsLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : statsError ? (
        <EmptyState title="Failed to load reports" description="Check your connection or try refreshing." />
      ) : (
        <>
          {(isSuperAdmin || user?.department === 'sales') && (
            <>
              {/* Overview KPIs */}
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
                <Kpi label="Total Leads" value={overview?.totalLeads ?? 0} />
                <Kpi label="Leads Today" value={overview?.leadsToday ?? 0} />
                <Kpi label="This Week" value={overview?.leadsThisWeek ?? 0} />
                <Kpi label="Converted" value={overview?.converted ?? 0} />
                <Kpi label="Employees" value={overview?.totalEmployees ?? 0} />
                <Kpi label="Scans Today" value={overview?.jobsToday ?? 0} />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card>
                  <CardHeader title={<span className="flex items-center gap-2"><PieChart className="h-4 w-4" /> Lead Status Breakdown</span>} />
                  <CardBody>
                    {statusData.length === 0 ? (
                      <EmptyState title="No lead data" description="Lead status data will appear here." />
                    ) : (
                      <BarChart data={statusData} xKey="status" yKey="n" color="#6E62FF" height={260} />
                    )}
                  </CardBody>
                </Card>
                <Card>
                  <CardHeader title={<span className="flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Lead Activity (last 14 days)</span>} />
                  <CardBody>
                    {activityData.length === 0 ? (
                      <EmptyState title="No activity yet" description="Daily lead activity will appear here." />
                    ) : (
                      <LineChart data={activityData} xKey="day" yKey="n" color="#6E62FF" height={260} />
                    )}
                  </CardBody>
                </Card>
              </div>
            </>
          )}

          {/* Team Performance leaderboard */}
          <Card>
            <CardHeader title={<span className="flex items-center gap-2"><Users className="h-4 w-4" /> Team Performance</span>} />
            <CardBody>
              <Table columns={leaderboardColumns} data={leaderboard} rowKey={(r) => r.id} empty="No team data available." />
            </CardBody>
          </Card>

          {/* Work Hours by date */}
          <Card>
            <CardHeader
              title={<span className="flex items-center gap-2"><Clock className="h-4 w-4" /> Work Hours</span>}
              action={
                <div className="w-44">
                  <Input
                    name="work-hours-date"
                    type="date"
                    value={whDate}
                    onChange={(e) => setWhDate(e.target.value || todayISO())}
                  />
                </div>
              }
            />
            <CardBody>
              {whLoading ? (
                <div className="flex h-24 items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <Table
                  columns={workHoursColumns}
                  data={workHours?.rows ?? []}
                  rowKey={(r) => r.id}
                  empty="No check-ins recorded for this date."
                />
              )}
            </CardBody>
          </Card>

          {/* Company report — SuperAdmin only */}
          {isSuperAdmin && (
            <Card>
              <CardHeader title={<span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Company Report</span>} />
              <CardBody className="space-y-4">
                {companyLoading ? (
                  <div className="flex h-24 items-center justify-center">
                    <Spinner />
                  </div>
                ) : company ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                      <Kpi label="Active Deals" value={company.overview?.dealsActive ?? 0} />
                      <Kpi label="Deals Won (₹)" value={Number(company.overview?.dealsWon ?? 0).toLocaleString('en-IN')} />
                      <Kpi label="Active Projects" value={company.overview?.activeProjects ?? 0} />
                      <Kpi label="Open Tasks" value={company.overview?.openTasks ?? 0} />
                      <Kpi label="Total Staff" value={company.overview?.totalStaff ?? 0} />
                      <Kpi label="On Leave" value={company.overview?.onLeave ?? 0} />
                      <Kpi label="Pending Leaves" value={company.overview?.pendingLeaves ?? 0} />
                      <Kpi label="Contacts" value={company.overview?.contacts ?? 0} />
                    </div>
                    <Table columns={deptColumns} data={company.byDepartment ?? []} rowKey={(r) => r.department ?? 'unknown'} empty="No department data." />
                  </>
                ) : (
                  <EmptyState title="No company data" description="Company report will appear here." />
                )}
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
